import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import os from './Orders.module.css'
import ls from './Loads.module.css'
import st from './Stock.module.css'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer } from '../../components/layers'
import { hasCapability, useAccess } from '../../lib/access'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { PalletDrawer } from './PalletDrawer'
import { CreatePalletsPanel } from './CreatePalletsPanel'
import { DispatchPalletsPanel } from './DispatchPalletsPanel'
import { OrderEditor } from './OrderEditor'
import { CloseButton } from './SdPanel'
import { DSTATE, HOLD_CHIP, PSTATE, digits, fmtDay, fmtStamp, lidText, lidWrong, srcShort } from './model'
import {
  TONE,
  approvalChip,
  approveLine,
  destinationText,
  freightDirty,
  freightErrors,
  freightFacts,
  freightForm,
  modeText,
  money,
  recordActions,
  selfApprovalNote,
  shortNote,
  sourceLabel,
  stateChips,
  summaryText,
  todayIso,
  type Chip,
  type CompatibleSource,
  type FreightForm,
  type OrderLine,
  type OrderRecord,
} from './orders'
import { withCurrentRef } from './loads'
import { openableRow } from '../../lib/openableRow'

// The order record (V4 order detail): the agreement, what is reserved
// against it, the pallets and loads serving it, and its history. Every move
// is a POST the API validates again; the record reloads what it returns.
// The Details tab becomes the order editor on Edit; a new order (orderId
// null) is only the editor until it is first saved. An export order's
// shipment references are edited on the Dispatches & documents tab, apart
// from the order's revision (FRT-1).

type Tab = 'details' | 'alloc' | 'disp' | 'inv' | 'hist'

const TABS: [Tab, string][] = [
  ['details', 'Details'],
  ['alloc', 'Allocation & pallets'],
  ['disp', 'Dispatches & documents'],
  ['inv', 'Invoicing'],
  ['hist', 'History'],
]

const errText = (e: any) => e?.body?.error || e?.message || String(e)

const ChipEl = ({ c }: { c: Chip }) => (
  <span className={styles.chip} style={{ background: c.bg, color: c.ink }}>
    {c.label}
  </span>
)

const toneChip = (label: string, tone: string): Chip => ({ label, bg: TONE[tone][0], ink: TONE[tone][1] })

export function OrderModal({
  user,
  orderId: openedId,
  onClose,
  onChanged,
  onNotice,
}: {
  user: any
  // null opens New order
  orderId: string | null
  onClose: () => void
  onChanged: (message?: string) => void
  // a message that changes nothing (Copy summary)
  onNotice?: (message: string) => void
}) {
  const [orderId, setOrderId] = useState<string | null>(openedId)
  const [editing, setEditing] = useState(openedId === null)
  const [rec, setRec] = useState<OrderRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('details')
  const [panel, setPanel] = useState<null | 'approve' | 'return'>(null)
  const [note, setNote] = useState('')
  // The move in flight, so only the pressed button says what it is doing.
  const [busyKind, setBusyKind] = useState<'submit' | 'approve' | 'return' | null>(null)
  const busy = busyKind !== null
  const slow = useSlow(busy)
  const [lineId, setLineId] = useState<string | null>(null)
  const [palletId, setPalletId] = useState<string | null>(null)
  const [createFor, setCreateFor] = useState<{ sourceId: string; allocationId: string } | null>(null)
  const [helpOn, toggleHelp] = useHelpPref(user, 'order_record')
  const access = useAccess()
  const mayOrder = hasCapability(access, 'create_edit_orders')
  const mayApprove = hasCapability(access, 'approve_orders')

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    if (!orderId) return
    try {
      setRec(await apiJson<OrderRecord>(userRef.current, `/api/orders/${orderId}`))
      setError(null)
    } catch (e) {
      setError(`Failed to load order: ${errText(e)}`)
    }
  }, [orderId])

  useEffect(() => {
    load()
  }, [load])

  // Escape closes the topmost layer only (h 425-458): child panels, then
  // panels on the editor, then the editor itself, then the record.
  const modalEl = useRef<HTMLDivElement>(null)
  const modal = useLayer(true, onClose, { el: modalEl, modal: true, busy })

  const move = async (action: 'submit' | 'approve' | 'return', body: Record<string, unknown> = {}) => {
    setBusyKind(action)
    try {
      await apiJson(userRef.current, `/api/orders/${orderId}/${action}`, { method: 'POST', body })
      setPanel(null)
      setNote('')
      await load()
      onChanged(action === 'submit' ? 'Submitted for approval.' : action === 'approve' ? 'Order approved.' : 'Returned to the author.')
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusyKind(null)
    }
  }

  const copySummary = () => {
    if (!rec) return
    try {
      navigator.clipboard?.writeText(summaryText(rec))
    } catch {
      /* the clipboard can be refused; the notice still says what was meant */
    }
    onNotice?.('Customer summary copied: no contractor notes, no prices')
  }

  const saved = async (r: { id: string; reference: string; approved: boolean }) => {
    setEditing(false)
    setTab('details')
    if (r.id !== orderId) setOrderId(r.id)
    else await load()
    onChanged(r.reference + (r.approved ? ' saved and approved.' : ' saved.'))
  }

  const isNew = !orderId
  const o = isNew ? undefined : rec?.order
  const acts = o && !editing ? recordActions(o.approval_status, rec?.approval) : null
  const selfNote = o && !editing && (o.approval_status === 'awaiting_approval' || o.approval_status === 'reapproval_required') ? selfApprovalNote(rec?.approval) : null
  const lines = rec?.lines || []
  const line = lines.find((l) => l.id === lineId) || lines[0] || null
  const [alloc, prep, disp, inv] = rec ? stateChips(rec.states, 'record') : []
  const today = todayIso()

  // Inline panels: return and approve (h 425-458), layers over the record.
  // A typed return comment is a change, so closing it then asks first.
  const returnPanel = useLayer(panel === 'return', () => setPanel(null), { dirty: !!note.trim() && !busy, busy })
  const approvePanel = useLayer(panel === 'approve', () => setPanel(null), { busy })

  return (
    <div className={os.backdrop} onClick={modal.requestClose}>
      <div
        ref={modalEl}
        className={os.record}
        role="dialog"
        aria-label={isNew ? 'New order' : o ? `Order ${o.reference}` : 'Order'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={os.recHead}>
          <div className={os.recTitleRow}>
            <span className={os.recTitle}>{isNew ? 'New order' : o?.reference || 'Order'}</span>
            {isNew && <ChipEl c={toneChip('Not yet saved', 'neutral')} />}
            {o && (
              <>
                <ChipEl c={approvalChip(o.approval_status)} />
                <ChipEl c={alloc} />
                <ChipEl c={prep} />
                <ChipEl c={disp} />
                <ChipEl c={inv} />
              </>
            )}
            <div className={styles.dHeadRight}>
              {o && !editing && o.approval_status === 'approved' && (
                <button type="button" className={pa.ghostBtn} title="Approved data only — no contractor notes or prices" onClick={copySummary}>
                  Copy summary
                </button>
              )}
              {o && !editing && mayOrder && (
                <button
                  type="button"
                  className={pa.ghostBtn}
                  onClick={() => {
                    setPanel(null)
                    setTab('details')
                    setEditing(true)
                  }}
                >
                  Edit
                </button>
              )}
              {acts?.submit && mayOrder && (
                <PendingButton className={pa.primaryBtn} busy={busyKind === 'submit'} pending="Submitting…" disabled={busy} onClick={() => move('submit')}>
                  Submit for approval
                </PendingButton>
              )}
              {acts?.ret && mayApprove && (
                <button type="button" className={styles.dangerBtn} disabled={busy} onClick={() => setPanel(panel === 'return' ? null : 'return')}>
                  Return for changes
                </button>
              )}
              {acts?.approve && (
                <button type="button" className={pa.primaryBtn} disabled={busy} onClick={() => setPanel(panel === 'approve' ? null : 'approve')}>
                  Review and approve
                </button>
              )}
              <CloseButton title="Close: the orders list stays behind" disabled={busy} onClick={modal.requestClose} />
            </div>
          </div>
          {o && (
            <>
              <div className={os.recSub}>
                {(o.customer?.name || '—') + ' → ' + (o.destination?.name || '—') + ' · '}
                {o.requested_dispatch_date ? fmtDay(o.requested_dispatch_date) + (o.requested_dispatch_date > today ? ' (provisional)' : '') : 'Not scheduled'}
                {' · ' + modeText(o.transport_mode, o.local_export)}
              </div>
            </>
          )}
          {rec?.exceptions.map((x, i) => (
            <div key={i} className={styles.alert}>
              {x}
            </div>
          ))}
          {o?.approval_status === 'returned_for_changes' && o.return_note && <div className={styles.amber}>Returned: {o.return_note}</div>}
          {selfNote && (
            <div className={styles.amber} data-testid="self-approval">
              {selfNote}
            </div>
          )}
          {error && (
            <div className={styles.alert} role="alert">
              {error}
            </div>
          )}
          {panel === 'return' && (
            <div className={os.inlinePanel} data-testid="return-panel">
              <div className={styles.strong}>Return to the author with a comment</div>
              <textarea
                className={os.textarea}
                aria-label="Return comment"
                placeholder="What needs to change before this can be approved"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className={styles.actions}>
                <PendingButton className={styles.dangerFill} busy={busyKind === 'return'} pending="Returning…" disabled={busy || !note.trim()} onClick={() => move('return', { comment: note.trim() })}>
                  Return order
                </PendingButton>
                <button type="button" className={pa.ghostBtn} disabled={busy} onClick={returnPanel.requestClose}>
                  Cancel
                </button>
              </div>
              {returnPanel.confirm}
            </div>
          )}
          {panel === 'approve' && o && (
            <div className={os.inlinePanel} data-testid="approve-panel">
              <div className={styles.strong}>Approve this revision</div>
              <div className={styles.sub}>
                {(o.customer?.name || '—') + ' → ' + (o.destination?.name || '—') + ' · ' + (o.requested_dispatch_date ? fmtDay(o.requested_dispatch_date) : 'Not scheduled') + ' · ' + modeText(o.transport_mode, o.local_export)}
              </div>
              <ul className={os.approveList}>
                {lines.map((l) => (
                  <li key={l.id}>{approveLine(l)}</li>
                ))}
              </ul>
              {o.approval_status === 'reapproval_required' && o.changes && o.changes.labels.length > 0 && (
                <div className={os.changedList}>Changed since the approved revision: {o.changes.labels.join('; ')}</div>
              )}
              <div className={styles.actions}>
                <PendingButton className={pa.primaryBtn} busy={busyKind === 'approve'} pending="Approving…" disabled={busy} onClick={() => move('approve', { revision: o.revision })}>
                  Approve
                </PendingButton>
                <button type="button" className={pa.ghostBtn} disabled={busy} onClick={approvePanel.requestClose}>
                  Not yet
                </button>
              </div>
              {approvePanel.confirm}
            </div>
          )}
          <div className={os.recTabs}>
            <div className={pa.tabTrack} role="tablist" aria-label="Order record">
              {/* the editor is the Details tab; leaving it would drop the edit */}
              {TABS.filter(([k]) => !(isNew || editing) || k === 'details').map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? pa.tabOn : pa.tab} onClick={() => setTab(k)}>
                  {l}
                </button>
              ))}
            </div>
            <button type="button" className={pa.helpBtn} style={{ marginLeft: 'auto' }} onClick={toggleHelp}>
              {helpOn ? 'Hide help' : 'How this tab works'}
            </button>
          </div>
        </div>

        <div className={`${styles.dBody} ${inertClass(busy, slow)}`}>
          {helpOn && <TabHelp tab={tab} onClose={toggleHelp} />}
          {(isNew || (rec && editing)) && tab === 'details' && (
            <OrderEditor
              key={orderId || 'new'}
              user={user}
              rec={isNew ? null : rec}
              onCancel={() => (isNew ? onClose() : setEditing(false))}
              onSaved={saved}
              onReview={() => {
                setEditing(false)
                load()
              }}
            />
          )}
          {!isNew && rec && !editing && tab === 'details' && <DetailsTab rec={rec} />}
          {rec && tab === 'alloc' && line && (
            <AllocationTab
              user={user}
              rec={rec}
              line={line}
              onPickLine={setLineId}
              onChanged={async (msg) => {
                await load()
                onChanged(msg)
              }}
              onOpenPallet={setPalletId}
              onCreate={setCreateFor}
            />
          )}
          {rec && tab === 'alloc' && !line && <div className={pa.empty}>This order has no lines.</div>}
          {rec && tab === 'disp' && (
            <DispatchTab
              user={user}
              rec={rec}
              onSaved={async (msg) => {
                await load()
                onChanged(msg)
              }}
              onReload={load}
            />
          )}
          {rec && tab === 'inv' && (
            <InvoicingTab
              user={user}
              rec={rec}
              onChanged={async (msg) => {
                await load()
                onChanged(msg)
              }}
            />
          )}
          {rec && tab === 'hist' && <HistoryTab rec={rec} />}
        </div>
      </div>

      {palletId && (
        <PalletDrawer
          user={user}
          palletId={palletId}
          onClose={() => setPalletId(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
        />
      )}
      {createFor && (
        <CreatePalletsPanel
          user={user}
          sourceId={createFor.sourceId}
          allocationId={createFor.allocationId}
          onClose={() => setCreateFor(null)}
          onCreated={(msg) => {
            setCreateFor(null)
            load()
            onChanged(msg)
          }}
        />
      )}
      {modal.confirm}
    </div>
  )
}

function DetailsTab({ rec }: { rec: OrderRecord }) {
  const o = rec.order
  // Price / box only for a caller with the pricing capability (§7n.9, PRICE-1).
  const pricing = !!rec.pricing
  const grid = pricing ? os.linesGridPriced : os.linesGrid
  return (
    <>
      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={`${grid} ${styles.head}`}>
          <span>Product line</span>
          <span>Required lid</span>
          <span className={styles.right}>Requested</span>
          <span className={styles.right}>Reserved</span>
          <span className={styles.right}>Prepared</span>
          <span className={styles.right}>Dispatched</span>
          <span className={styles.right}>Outstanding</span>
          {pricing && <span className={styles.right}>Price / box</span>}
          <span>Source instructions</span>
        </div>
        {rec.lines.map((l) => (
          <div key={l.id} className={`${grid} ${styles.line}`} data-testid="order-line">
            <span className={styles.strong}>
              {l.variety + ' · ' + l.box_type}
              {l.changed && <span className={os.changedChip}>Changed</span>}
            </span>
            <span className={l.lid ? undefined : styles.amberText}>{l.lid?.name || 'Not specified'}</span>
            <span className={styles.right}>{l.requested}</span>
            <span className={styles.right}>{l.reserved}</span>
            <span className={styles.right}>{l.prepared}</span>
            <span className={styles.right}>{l.dispatched}</span>
            <span className={`${styles.right} ${styles.strong}`}>{l.outstanding}</span>
            {pricing && (
              <span className={l.price_per_box == null ? `${styles.right} ${styles.amberText}` : styles.right}>
                {l.price_per_box == null ? 'None' : money(l.price_per_box)}
              </span>
            )}
            <span className={styles.muted}>{l.instructions || '—'}</span>
          </div>
        ))}
      </div>
      <div className={os.cards3}>
        <div className={os.infoCard}>
          <span className={styles.section}>Destination</span>
          <div>{destinationText(o.destination)}</div>
          {o.final_destination && <div data-testid="final-destination">Final destination: {o.final_destination}</div>}
        </div>
        <div className={os.infoCard}>
          <span className={styles.section}>Customer PO</span>
          <div>{o.customer_po || 'None'}</div>
        </div>
        <div className={os.infoCard}>
          <span className={styles.section}>Internal instructions</span>
          <div>{o.internal_notes || 'None'}</div>
        </div>
      </div>
    </>
  )
}

function AllocationTab({
  user,
  rec,
  line,
  onPickLine,
  onChanged,
  onOpenPallet,
  onCreate,
}: {
  user: any
  rec: OrderRecord
  line: OrderLine
  onPickLine: (id: string) => void
  onChanged: (msg: string) => Promise<void>
  onOpenPallet: (id: string) => void
  onCreate: (c: { sourceId: string; allocationId: string }) => void
}) {
  const [sources, setSources] = useState<CompatibleSource[] | null>(null)
  const [pick, setPick] = useState<string | null>(null)
  const [qty, setQty] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  // 'reserve', or the stock source whose reservation is being released.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const o = rec.order
  const approved = o.approval_status === 'approved'
  const access = useAccess()
  const mayOrder = hasCapability(access, 'create_edit_orders')
  const mayManage = hasCapability(access, 'manage_stock_dispatch')

  const userRef = useRef(user)
  userRef.current = user
  const loadSources = useCallback(async () => {
    try {
      const r = await apiJson<{ sources: CompatibleSource[] }>(userRef.current, `/api/orders/${o.id}/lines/${line.id}/compatible`)
      setSources(r.sources)
    } catch (e) {
      setSources([])
      setMsg({ ok: false, text: `Failed to load compatible stock: ${errText(e)}` })
    }
  }, [o.id, line.id])

  useEffect(() => {
    setPick(null)
    setQty('')
    setMsg(null)
    loadSources()
  }, [loadSources])

  const chosen = sources?.find((s) => s.id === pick) || null
  const n = Number(qty) || 0
  const canReserve = !!chosen && !busy && n > 0 && n <= chosen.available && n <= line.unallocated

  const reserve = async () => {
    if (!chosen) return
    setBusyOn('reserve')
    try {
      await apiJson(userRef.current, `/api/orders/${o.id}/lines/${line.id}/allocate`, {
        method: 'POST',
        body: { stock_source_id: chosen.id, boxes: n },
      })
      const text = `Reserved ${n} boxes from ${sourceLabel(chosen)}.`
      setMsg({ ok: true, text })
      setPick(null)
      setQty('')
      await onChanged(text)
    } catch (e: any) {
      setMsg({ ok: false, text: e?.body?.notice || errText(e) })
    } finally {
      setBusyOn(null)
      loadSources()
    }
  }

  const release = async (sourceId: string) => {
    setBusyOn(sourceId)
    try {
      await apiJson(userRef.current, `/api/orders/${o.id}/lines/${line.id}/release`, { method: 'POST', body: { stock_source_id: sourceId } })
      setMsg({ ok: true, text: 'Reservation released.' })
      await onChanged('Reservation released.')
    } catch (e) {
      setMsg({ ok: false, text: errText(e) })
    } finally {
      setBusyOn(null)
      loadSources()
    }
  }

  const short = shortNote(line)
  const pallets = rec.pallets.filter((p) => p.order?.line_id === line.id)
  const fact = (k: string, v: number, red = false) => (
    <div className={os.factCard}>
      <span className={styles.factK}>{k}</span>
      <span className={os.factV} style={red ? { color: '#8a2f1d' } : undefined}>
        {v}
      </span>
    </div>
  )

  return (
    <>
      {rec.lines.length > 1 && (
        <div className={pa.tabTrack} role="tablist" aria-label="Order line" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
          {rec.lines.map((l) => (
            <button key={l.id} type="button" role="tab" aria-selected={l.id === line.id} className={l.id === line.id ? pa.tabOn : pa.tab} onClick={() => onPickLine(l.id)}>
              {l.variety + ' · ' + l.box_type + ' · ' + l.requested}
            </button>
          ))}
        </div>
      )}
      <div className={os.factRow}>
        {fact('Requested', line.requested)}
        {fact('Reserved', line.reserved)}
        {fact('Prepared', line.prepared)}
        {fact('Dispatched', line.dispatched)}
        {fact('Still unfulfilled', line.outstanding)}
        {fact('Unallocated shortage', line.unallocated, line.unallocated > 0)}
      </div>
      <div className={styles.muted} style={{ fontSize: '0.7813rem', flex: 'none' }}>
        Required lid: <strong>{line.lid?.name || 'Not specified'}</strong> · Instructions: {line.instructions || o.internal_notes || 'No source instructions'}
      </div>
      {short && <div className={styles.alert}>{short}</div>}

      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={os.cardTitle}>{mayOrder ? 'Compatible received stock: pick a group to reserve from' : 'Compatible received stock'}</div>
        <div className={`${os.srcGrid} ${styles.head}`}>
          <span>Harvest</span>
          <span>Farm / block</span>
          <span>Contractor</span>
          <span>Lid at picking</span>
          <span className={styles.right}>/plt</span>
          <span className={styles.right}>Available</span>
        </div>
        {sources && sources.length === 0 && (
          <div className={pa.empty}>
            No stock on hand matches {line.variety} in {line.box_type}. Stock arrives through packhouse receipts only.
          </div>
        )}
        {(sources || []).map((s) => (
          <div
            key={s.id}
            className={mayOrder ? `${os.srcGrid} ${styles.line} ${styles.clickable}` : `${os.srcGrid} ${styles.line}`}
            style={s.id === pick ? { background: 'var(--dp-accent-soft)' } : undefined}
            data-testid="compatible-row"
            {...(mayOrder
              ? openableRow(() => {
                  setPick(s.id)
                  setQty(String(Math.max(0, Math.min(s.available, line.unallocated))))
                  setMsg(null)
                })
              : {})}
          >
            <span>{s.harvest_date ? fmtDay(s.harvest_date) : '—'}</span>
            <span className={styles.ellipsis}>{s.block_name ? (s.site_name || '—') + ' · ' + s.block_name : s.site_name || '—'}</span>
            <span className={styles.ellipsis}>{s.contractor_name || '—'}</span>
            <span className={styles.stack}>
              <span>{s.lid_name || '—'}</span>
              {s.lid_change && <span className={styles.amberText}>Lid change needed</span>}
            </span>
            <span className={styles.right}>{s.capacity_per_pallet ?? '—'}</span>
            <span className={`${styles.right} ${styles.stack}`}>
              <span className={styles.strong}>{s.available}</span>
              {s.held > 0 && <span style={{ color: '#8a2f1d', fontSize: '0.6875rem' }}>{s.held} held</span>}
            </span>
          </div>
        ))}
        {chosen && mayOrder && (
          <div className={os.reserveBar}>
            <span className={styles.strong}>{sourceLabel(chosen) + ' · ' + chosen.available + ' available'}</span>
            <input className={styles.qtyInput} aria-label="Boxes to reserve" inputMode="numeric" value={qty} onChange={(e) => setQty(digits(e.target.value))} />
            <PendingButton className={pa.primaryBtn} busy={busyOn === 'reserve'} pending="Reserving…" disabled={!canReserve} onClick={reserve}>
              Reserve
            </PendingButton>
          </div>
        )}
        {msg && (
          <div className={msg.ok ? os.okMsg : os.errMsg} role={msg.ok ? 'status' : 'alert'}>
            {msg.text}
          </div>
        )}
      </div>

      <div className={pa.card} style={{ flex: 'none' }}>
        <div className={os.cardTitle}>Reservations on this line</div>
        {!approved && line.reservations.length > 0 && (
          <div className={styles.amber} style={{ margin: '0 0.875rem 0.625rem' }}>
            Pallet creation from this order waits for approval (preparation release gate).
          </div>
        )}
        {line.reservations.length === 0 && <div className={pa.empty}>Nothing reserved yet.</div>}
        {line.reservations.map((r) => (
          <div key={r.allocation_id} className={`${os.resRow} ${styles.line}`} data-testid="reservation-row">
            <span className={styles.ellipsis}>{sourceLabel(r.source)}</span>
            {r.provisional && <span className={styles.amberText}>Provisional</span>}
            <span className={styles.strong}>{r.boxes} boxes</span>
            <span className={styles.actions} style={{ marginLeft: 'auto' }}>
              {approved && mayManage && (
                <button type="button" className={pa.primaryBtn} onClick={() => onCreate({ sourceId: r.source.id, allocationId: r.allocation_id })}>
                  Create pallets
                </button>
              )}
              {mayOrder && (
                <PendingButton className={pa.ghostBtn} busy={busyOn === r.source.id} pending="Releasing…" disabled={busy} onClick={() => release(r.source.id)}>
                  Release
                </PendingButton>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={os.cardTitle}>Assigned pallets</div>
        {pallets.length === 0 && <div className={pa.empty}>No pallets yet. Create them from a reservation above.</div>}
        {pallets.map((p) => {
          const st: Chip = p.departed ? toneChip('Departed', 'ok') : { label: PSTATE[p.state][0], bg: PSTATE[p.state][1], ink: PSTATE[p.state][2] }
          return (
            <div key={p.id} className={`${os.palGrid} ${styles.line} ${styles.clickable}`} data-testid="order-pallet" {...openableRow(() => onOpenPallet(p.id))}>
              <span className={styles.strong}>{p.pallet_id}</span>
              <span>{p.boxes} boxes</span>
              <span className={`${styles.sub} ${styles.ellipsis}`}>{srcShort(p.source)}</span>
              <span>
                <ChipEl c={st} />
              </span>
              <span className={lidWrong(p) ? styles.lidBad : undefined}>{lidText(p)}</span>
              <span className={styles.muted}>{p.location?.name || '—'}</span>
              <span>{p.hold && <ChipEl c={{ label: HOLD_CHIP[0], bg: HOLD_CHIP[1], ink: HOLD_CHIP[2] }} />}</span>
              <span className={styles.muted}>{p.dispatch && p.dispatch.state !== 'cancelled' ? p.dispatch.reference : '—'}</span>
            </div>
          )
        })}
      </div>
    </>
  )
}

function DispatchTab({
  user,
  rec,
  onSaved,
  onReload,
}: {
  user: any
  rec: OrderRecord
  onSaved: (msg: string) => Promise<void>
  onReload: () => Promise<void>
}) {
  const [dispatching, setDispatching] = useState(false)
  // Dispatch from here (Adrian and Vanessa, 2026-11-02): an approved order with pallets not yet departed.
  const mayDispatch =
    hasCapability(useAccess(), 'manage_stock_dispatch') && rec.order.approval_status === 'approved' && rec.pallets.some((p) => !p.departed)
  return (
    <>
      <div className={styles.strong} style={{ flex: 'none' }}>
        {rec.boxes.outstanding} of {rec.boxes.ordered} boxes outstanding
      </div>
      <div className={pa.card} style={{ flex: 'none' }}>
        <div className={os.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Loads carrying this order</span>
          {mayDispatch && (
            <button type="button" className={pa.primaryBtn} style={{ marginLeft: 'auto' }} onClick={() => setDispatching(true)}>
              Dispatch pallets
            </button>
          )}
        </div>
        {rec.dispatches.length === 0 && (
          <div className={pa.empty}>No load yet. Dispatch this order’s pallets here, or add them to a load from Dispatches.</div>
        )}
        {rec.dispatches.map((d) => (
          <div key={d.id} className={`${os.resRow} ${styles.line}`} data-testid="order-load">
            <span className={styles.strong}>{d.reference}</span>
            <span className={styles.muted}>{fmtStamp(d.actual_departure || d.planned_departure) || 'No departure set'}</span>
            <span>
              {d.pallets} pallet{d.pallets === 1 ? '' : 's'} · {d.boxes} boxes
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <ChipEl c={toneChip(DSTATE[d.state] || d.state, d.state === 'dispatched' ? 'ok' : d.state === 'cancelled' ? 'warn' : 'neutral')} />
            </span>
          </div>
        ))}
      </div>
      {rec.freight.applies && <FreightCard user={user} rec={rec} onSaved={onSaved} onReload={onReload} />}
      <div className={styles.note} style={{ flex: 'none' }}>
        Delivery docket, packing list and transfer record are not deployed yet.
        {rec.freight.applies && ' The receiving establishment is not stored yet: whether it belongs to the order or to the destination is still open (CUST-3).'}
      </div>
      {dispatching && (
        <DispatchPalletsPanel
          user={user}
          orderId={rec.order.id}
          orderRef={rec.order.reference}
          pallets={rec.pallets}
          onCancel={() => setDispatching(false)}
          onDone={async (msg) => {
            setDispatching(false)
            await onSaved(msg)
          }}
        />
      )}
    </>
  )
}

// Shipment references (§7n.6): on the order, never the truck, and outside
// the order's revision, so saving one never costs the approval (FRT-1). A
// save carries the references as the editor opened them; a save over
// someone else's change is refused, naming them.
function FreightCard({
  user,
  rec,
  onSaved,
  onReload,
}: {
  user: any
  rec: OrderRecord
  onSaved: (msg: string) => Promise<void>
  onReload: () => Promise<void>
}) {
  const f = rec.freight
  const [form, setForm] = useState<FreightForm | null>(null)
  const [opened, setOpened] = useState<FreightForm | null>(null)
  const [carriers, setCarriers] = useState<{ id: string; name: string }[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const mayOrder = hasCapability(useAccess(), 'create_edit_orders')

  const edit = async () => {
    const start = freightForm(f)
    setForm(start)
    setOpened(start)
    setError(null)
    try {
      const o = await apiJson<{ carriers: { id: string; name: string }[] }>(user, '/api/dispatches/options')
      setCarriers(o.carriers)
    } catch (e) {
      setError(`Failed to load carriers: ${errText(e)}`)
    }
  }

  const save = async () => {
    if (!form || !opened) return
    setBusy(true)
    try {
      await apiJson(user, `/api/orders/${rec.order.id}/freight`, {
        method: 'PUT',
        body: {
          forwarder_id: form.forwarder_id || null,
          airway_bill: form.airway_bill,
          flight: form.flight,
          container: form.container,
          before: { ...opened, forwarder_id: opened.forwarder_id || null },
        },
      })
      setForm(null)
      setError(null)
      await onSaved('Freight references saved.')
    } catch (e: any) {
      if (e?.body?.code === 'STALE') {
        // Show the newer references before anyone saves over them.
        setForm(null)
        setError(errText(e))
        await onReload()
      } else setError(e?.body?.errors?.join(' ') || errText(e))
    } finally {
      setBusy(false)
    }
  }

  const choices = withCurrentRef(carriers, f.forwarder)
  const problems = form ? freightErrors(form) : []
  const text = (k: 'airway_bill' | 'flight' | 'container', label: string) => (
    <label className={ls.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input className={styles.input} value={form![k]} onChange={(e) => setForm({ ...form!, [k]: e.target.value })} />
    </label>
  )

  return (
    <div className={pa.card} style={{ flex: 'none' }} data-testid="freight-card">
      <div className={os.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Shipment references — stored on this order, not the truck</span>
        {!form && mayOrder && (
          <button type="button" className={pa.ghostBtn} style={{ marginLeft: 'auto' }} onClick={edit}>
            Edit references
          </button>
        )}
      </div>
      {!form && (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className={styles.facts}>
            {freightFacts(f).map((x) => (
              <div key={x.k} className={styles.fact} data-testid="freight-fact">
                <span className={styles.factK}>{x.k}</span>
                <span>{x.v}</span>
              </div>
            ))}
          </div>
          {f.updated && <div className={styles.note}>{'Last changed by ' + (f.updated.by || 'unknown') + ' · ' + fmtStamp(f.updated.at)}</div>}
          {error && (
            <div className={styles.alert} role="alert">
              {error}
            </div>
          )}
        </div>
      )}
      {form && (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className={`${ls.form} ${inertClass(busy, slow)}`}>
            <label className={ls.field}>
              <span className={styles.fieldLabel}>Freight forwarder</span>
              <select className={styles.input} value={form.forwarder_id} onChange={(e) => setForm({ ...form, forwarder_id: e.target.value })}>
                <option value="">None</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            {text('airway_bill', 'Airway bill')}
            {text('flight', 'Flight')}
            {text('container', 'Container')}
          </div>
          <div className={styles.note}>Forwarders come from the carriers list in Stock & Dispatch settings. Saving does not change the order’s revision or its approval.</div>
          {[...problems, ...(error ? [error] : [])].map((m) => (
            <div key={m} className={styles.alert} role="alert">
              {m}
            </div>
          ))}
          <div className={styles.actions}>
            <PendingButton className={pa.primaryBtn} busy={busy} pending="Saving…" disabled={problems.length > 0 || !opened || !freightDirty(form, opened)} onClick={save}>
              Save references
            </PendingButton>
            <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Invoicing (§7n.9, h 5521-5528): what departed, per product and dispatch,
// against what is on a draft. Create draft records the request before
// anything is sent; an unknown outcome holds its boxes until Check Xero
// before retrying settles it, and nothing here ever resends by itself. The
// draft carries no tax, account or currency (#25).
type InvLine = {
  line_id: string
  line_number: number
  variety: string
  box_type: string
  price_per_box: number | null
  dispatch_id: string
  dispatch_reference: string
  departed_at: string | null
  dispatched: number
  invoiced: number
  pending: number
  invoiceable: number
}
type Blocked = { code: string; message: string }
type InvRequest = {
  id: string
  reference: string
  dispatch: { id: string; reference: string } | null
  state: 'creating' | 'created' | 'failed' | 'unknown'
  label: string
  boxes: number
  code: string | null
  message: string | null
  xero_invoice_id: string | null
  requested: { by: string | null; at: string }
  settled: { by: string | null; at: string } | null
  checked_at: string | null
}
type Invoicing = {
  can_create: boolean
  connection: { connected: boolean; label: string }
  contact_linked: boolean
  lines: InvLine[]
  totals: { dispatched: number; invoiced: number; pending: number; invoiceable: number }
  blocked: Blocked[]
  dispatches: { id: string; reference: string; departed_at: string | null; invoiceable: number; blocked: Blocked[] }[]
  requests: InvRequest[]
}

const REQUEST_TONE: Record<InvRequest['state'], string> = { created: 'ok', failed: 'warn', unknown: 'amber', creating: 'neutral' }

function InvoicingTab({ user, rec, onChanged }: { user: any; rec: OrderRecord; onChanged: (msg: string) => Promise<void> }) {
  const [inv, setInv] = useState<Invoicing | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The press in flight: 'all', a dispatch id, or a request id being checked.
  const [busy, setBusy] = useState<string | null>(null)
  const id = rec.order.id

  const read = useCallback(async () => {
    try {
      setInv(await apiJson<Invoicing>(user, `/api/orders/${id}/invoicing`))
      setError(null)
    } catch (e) {
      setError(`Failed to load invoicing: ${errText(e)}`)
    }
  }, [user, id])

  useEffect(() => {
    if (rec.pricing) read()
  }, [rec.pricing, read])

  if (!rec.pricing) {
    return <div className={pa.empty}>Invoicing shows prices, so it needs the pricing capability, which your permission set does not include.</div>
  }

  const press = async (key: string, url: string, body?: Record<string, unknown>) => {
    setBusy(key)
    setError(null)
    try {
      const r = await apiJson<{ message: string; invoicing: Invoicing }>(user, url, { method: 'POST', body: body || {} })
      setInv(r.invoicing)
      await onChanged(r.message)
    } catch (e: any) {
      setError(e?.body?.blocked?.length ? e.body.blocked.map((b: Blocked) => b.message).join(' ') + ' Nothing was sent.' : errText(e))
      await read()
    } finally {
      setBusy(null)
    }
  }

  if (!inv) return error ? <div className={styles.alert} role="alert">{error}</div> : <div className={pa.empty}>Loading…</div>
  const t = inv.totals
  const blockedText = (b: Blocked[]) => b.map((x) => x.message).join(' ')
  const perLoad = inv.dispatches.filter((d) => d.invoiceable > 0)

  return (
    <>
      <div className={styles.strong} style={{ flex: 'none' }} data-testid="inv-summary">
        {t.dispatched} dispatched · {t.invoiced} invoiced{t.pending > 0 ? ` · ${t.pending} awaiting Xero` : ''} · {t.invoiceable} to invoice
      </div>
      <div className={styles.facts} style={{ flex: 'none' }}>
        <div className={styles.fact}>
          <span className={styles.factK}>{inv.connection.label}</span>
          <span>{inv.connection.connected ? 'Connected' : 'Not connected'}</span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factK}>Xero contact</span>
          <span>{inv.contact_linked ? 'Linked' : 'Not linked'}</span>
        </div>
      </div>

      <div className={pa.card} style={{ flex: 'none' }}>
        <div className={os.cardTitle}>Dispatched boxes by product and load</div>
        {inv.lines.length === 0 && <div className={pa.empty}>Nothing has departed yet, so there is nothing to invoice.</div>}
        {inv.lines.length > 0 && (
          <table className={st.table} data-testid="inv-lines">
            <thead>
              <tr>
                <th>Product</th>
                <th>Load</th>
                <th className={st.num}>Dispatched</th>
                <th className={st.num}>Invoiced</th>
                <th className={st.num}>Awaiting Xero</th>
                <th className={st.num}>To invoice</th>
                <th className={st.num}>Price / box</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l) => (
                <tr key={`${l.line_id}|${l.dispatch_id}`}>
                  <td>
                    Line {l.line_number} · {l.variety} · {l.box_type}
                  </td>
                  <td>
                    {l.dispatch_reference}
                    <div className={styles.muted}>{fmtStamp(l.departed_at) || ''}</div>
                  </td>
                  <td className={st.num}>{l.dispatched}</td>
                  <td className={st.num}>{l.invoiced}</td>
                  <td className={st.num}>{l.pending}</td>
                  <td className={st.num}>{l.invoiceable}</td>
                  <td className={st.num}>{l.price_per_box == null ? 'Not priced' : money(l.price_per_box)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inv.can_create && (
        <div className={pa.card} style={{ flex: 'none' }} data-testid="inv-create">
          <div className={os.cardTitle}>Create a draft in Xero</div>
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className={styles.actions} style={{ alignItems: 'center' }}>
              <PendingButton className={pa.primaryBtn} busy={busy === 'all'} pending="Creating…" disabled={busy !== null || inv.blocked.length > 0} onClick={() => press('all', `/api/orders/${id}/invoices`)}>
                Create draft for all uninvoiced boxes
              </PendingButton>
              {inv.blocked.length === 0 && <span className={styles.muted}>{t.invoiceable} boxes</span>}
            </div>
            {inv.blocked.length > 0 && (
              <div className={styles.note} data-testid="inv-blocked">
                {blockedText(inv.blocked)}
              </div>
            )}
            {/* One load at a time only where there is a choice: two or more
                loads with boxes still to invoice. */}
            {perLoad.length > 1 &&
              perLoad.map((d) => (
                <div key={d.id} className={`${os.resRow} ${styles.line}`} data-testid="inv-dispatch">
                  <span className={styles.strong}>{d.reference}</span>
                  <span className={styles.muted}>{d.blocked.length ? blockedText(d.blocked) : `${d.invoiceable} boxes to invoice`}</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <PendingButton className={pa.ghostBtn} busy={busy === d.id} pending="Creating…" disabled={busy !== null || d.blocked.length > 0} onClick={() => press(d.id, `/api/orders/${id}/invoices`, { dispatch_id: d.id })}>
                      Draft this load
                    </PendingButton>
                  </span>
                </div>
              ))}
            <div className={styles.note}>
              The draft carries boxes and the price per box only. Tax, account codes and currency are left to Xero until they are settled (#25). Control never approves, sends or voids an invoice.
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className={styles.alert} role="alert" style={{ flex: 'none' }}>
          {error}
        </div>
      )}

      <div className={pa.card} style={{ flex: 'none' }}>
        <div className={os.cardTitle}>Draft requests</div>
        {inv.requests.length === 0 && <div className={pa.empty}>No draft has been requested for this order.</div>}
        {inv.requests.map((r) => (
          <div key={r.id} className={`${os.resRow} ${styles.line}`} data-testid="inv-request" style={{ flexWrap: 'wrap' }}>
            <span className={styles.strong}>{r.reference}</span>
            <span>
              {r.boxes} boxes{r.dispatch ? ` · ${r.dispatch.reference}` : ''}
            </span>
            <span className={styles.muted}>
              {'Requested by ' + (r.requested.by || 'unknown') + ' · ' + fmtStamp(r.requested.at)}
              {r.checked_at ? ' · checked ' + fmtStamp(r.checked_at) : ''}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <ChipEl c={toneChip(r.label, REQUEST_TONE[r.state])} />
            </span>
            {r.message && r.state !== 'created' && (
              <div className={r.state === 'failed' ? styles.alert : styles.note} style={{ flexBasis: '100%' }}>
                {r.message}
              </div>
            )}
            {r.state === 'unknown' && inv.can_create && (
              <div style={{ flexBasis: '100%' }}>
                <PendingButton className={pa.ghostBtn} busy={busy === r.id} pending="Checking…" disabled={busy !== null} onClick={() => press(r.id, `/api/invoices/${r.id}/check`)}>
                  Check Xero before retrying
                </PendingButton>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  )
}

function HistoryTab({ rec }: { rec: OrderRecord }) {
  if (rec.history.length === 0) return <div className={pa.empty}>No history yet.</div>
  return (
    <div className={styles.facts}>
      {rec.history.map((h) => (
        <div key={h.id} className={styles.hist} data-testid="order-history">
          <span className={styles.histAt}>{fmtStamp(h.at)}</span>
          <span className={styles.stack}>
            <span>{h.what}</span>
            <span className={styles.sub}>{h.by + ' · ' + h.how}</span>
            {h.reason && <span className={styles.sub}>{h.reason}</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

const HELP: Record<Tab, { title: string; items: [string, React.ReactNode][] }> = {
  details: {
    title: 'What this tab holds',
    items: [
      [
        'The commercial agreement',
        'Customer, destination, transport mode, dispatch date and the lines: variety, box type, required lid and quantity. This is what was agreed. It is not a record of what has been picked, packed or sent.',
      ],
      [
        'Editing can cost the approval',
        'Changing the customer, destination, dispatch date, transport mode, or a line’s product, quantity, lid or price on an approved order sends it back for reapproval and blocks preparation and labels until it is approved again. The PO, internal instructions and source instructions do not.',
      ],
      [
        'Price per box',
        'Each line carries the price per box agreed for it. A new line starts at the last price used for that variety and box type, and the field says so; change it if something else was agreed. A line saved untouched keeps that figure. Only people whose permission set covers pricing see or set prices; everyone else sees the order without them. Currency and tax treatment are not settled yet (#25).',
      ],
      [
        'Required lid matters here',
        'The lid is set on the line, and every pallet serving that line must carry it. Getting it wrong is a customer rejection, so the lid is part of the order rather than a packing detail.',
      ],
    ],
  },
  alloc: {
    title: 'How allocation and pallets work',
    items: [
      [
        'Reserving is a promise, not a movement',
        'Allocating boxes against a line reserves specific stock; the boxes stay in the source group and stay on hand. Nothing physical happens until a pallet is confirmed and a load departs.',
      ],
      [
        'Requested, reserved, prepared, dispatched',
        <div className={styles.helpLines}>
          <div>
            <strong>Requested</strong> what the customer asked for on this line
          </div>
          <div>
            <strong>Reserved</strong> boxes committed against specific stock
          </div>
          <div>
            <strong>Prepared</strong> boxes on confirmed pallets
          </div>
          <div>
            <strong>Dispatched</strong> boxes that physically left on a departed load
          </div>
          <div>The gap between reserved and prepared is work. The gap between requested and reserved is a shortage.</div>
        </div>,
      ],
      [
        'Stock has to match the line',
        'Only source groups with the line’s variety and box type can be reserved, and an approved order is normally required first. Pallets created from a reservation are locked to that source and that order.',
      ],
    ],
  },
  disp: {
    title: 'Loads and paperwork on this order',
    items: [
      [
        'Loads come from the pallets',
        'This tab lists the loads carrying this order’s pallets and what each took. An order with pallets on two trucks appears on both; nothing here is entered by hand.',
      ],
      [
        'Dispatch pallets',
        'Pick the carrier and untick any pallet that is not on the truck. Saving creates the load, verifies the pallets by sight and departs it at once: the boxes leave stock and count as dispatched. If a load with that carrier is already booked for today, the pallets can join it instead, and it departs from its load record in Dispatches.',
      ],
      [
        'Documents are Draft until departure',
        'Delivery docket, packing list and transfer record can be produced early, but they are marked Draft until the load departs, and the final version reflects what actually left.',
      ],
      [
        'Export details live on the order',
        'Airway bill, flight, container and forwarder belong here rather than on the load, so a shared truck never carries one customer’s paperwork onto another’s. They apply to export orders only, and saving them does not change the order’s approval.',
      ],
    ],
  },
  inv: {
    title: 'How invoicing works',
    items: [
      [
        'Only what departed is invoiced',
        'Lines are built per product and per dispatch from dispatched boxes. Reserved or palletised boxes are never invoiced, so a part-sent order invoices only the part that went.',
      ],
      [
        'Control drafts, Xero issues',
        'A draft is created in Xero for review; Control never approves, sends or voids an invoice. Only a created draft counts its boxes as invoiced, and the price it carried becomes the prefill for that product next time.',
      ],
      [
        'An unknown outcome holds its boxes',
        'If Xero does not answer, nobody knows whether the draft exists, so its boxes stay off every new draft and nothing is resent. Check Xero before retrying looks the draft up by its reference: found, it counts as created; not there, the boxes can be drafted again.',
      ],
      [
        'Pricing is a separate permission',
        'This tab shows prices, so it is only shown to users with the pricing capability. Creating a draft also needs Create Xero invoices, held by the same permission sets for now.',
      ],
    ],
  },
  hist: {
    title: 'What the history records',
    items: [
      [
        'Every state change, with a person',
        'Approvals, returns, edits, reservations, pallet confirmations and departures, each with the user, the timestamp and whether it came from Control or the crew app.',
      ],
      [
        'Nothing is edited away',
        'Entries are never rewritten or removed, including corrections and discrepancies. A revision number tracks how many times the order has materially changed.',
      ],
    ],
  },
}

function TabHelp({ tab, onClose }: { tab: Tab; onClose: () => void }) {
  const h = HELP[tab]
  return (
    <div className={styles.helpBox} data-testid="order-help">
      <button type="button" className={styles.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
        ×
      </button>
      <div className={styles.helpTitle}>{h.title}</div>
      {h.items.map(([k, v]) => (
        <div key={k} className={styles.helpItem}>
          <span>{k}</span>
          {typeof v === 'string' ? <p>{v}</p> : v}
        </div>
      ))}
    </div>
  )
}
